import React, { useEffect, useState } from 'react';
import { Row, Col, Button, Spinner } from 'reactstrap';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import BootstrapTable from 'react-bootstrap-table-next';
import { AssetsPropertyFormData } from '../../../Public/FormData';
import { RootState } from '../../../../../ReduxToolkit/Store';
import { useLocation } from 'react-router-dom';
import { useGetRequestQuery } from '../../../../../Hooks/useGetRequestQuery';
import { getAssetsPropertyByClientIdService, getAssetsPropertyByIdService, getClientFactFindDLLService, insertOrUpdateAssetsPropertyService, softDeleteAssetsPropertyService } from '../../../../../Services/PublicPageServices';
import { useMutationRequestQuery } from '../../../../../Hooks/useMutationRequestQuery';
import { H3, LI, UL } from '../../../../../AbstractElements';
import Dropdown from '../../../../common/DropDown';
import TextField from '../../../../common/TextField';
import DatePicker from '../../../../common/DatePicker';
import DollarInput from '../../../../common/DollarInput';
import DecimalInput from '../../../../common/DecimalInput';
import FactFindMotorVehicles from './FactFindMotorVehicles';
import FactFindLiabilitiesOthers from './FactFindLiabilitiesOthers';
import FactFindAssetsOther from './FactFindAssetsOther';
import FactFindIncomeExpense from './FactFindIncomeExpense';

export default function FactFindAssetsProperty({ clientId }: any) {
    const [formData, setFormData] = useState<any>(AssetsPropertyFormData);
    const [assetsPropertyData, setAssetsPropertyData] = useState([]);
    const [selectedRowId, setSelectedRowId] = useState<number | null>(null);
    const userData = useSelector((state: RootState) => state.auth);
    const location = useLocation();
    const isMMPAssetsLiabilitiesRoute = location.pathname === '/MMPAssetsLiabilities';
    const [clientFactsFindData, setClientFactFindData] = React.useState<any>(null);
    const [showForm, setShowForm] = useState(false)

    const {
        data: clientFactFindData,
        refetch: refetchClientFactFindData,
        isLoading: clientFactFindDataLoading,
        isError: clientFactFindDataIsError,
    } = useGetRequestQuery<any>(
        ['getClientFactFindDLL'],
        getClientFactFindDLLService,
        {
            enabled: true,
        }
    );

    useEffect(() => {
        if (clientFactFindData?.data && !clientFactFindDataIsError && !clientFactFindDataLoading) {
            setClientFactFindData(clientFactFindData?.data);
        }
    }, [clientFactFindData]);

    const {
        data: assetsProperty,
        refetch: refetchAssetsProperty,
        isLoading: assetsPropertyLoading,
        isError: assetsPropertyIsError,
    } = useGetRequestQuery(
        ['getAssetsPropertyByClientId', clientId],
        () => getAssetsPropertyByClientIdService(clientId as any),
        {
            enabled: true,
        }
    );
    const {
        data: assetsPropertyFormData,
        isLoading: assetsPropertyFormDataLoading,
        isError: assetsPropertyFormDataIsError,
    } = useGetRequestQuery(
        ["getAssetsPropertyById", selectedRowId],
        () => getAssetsPropertyByIdService(selectedRowId),
        {
            enabled: !!selectedRowId,
        }
    );

    useEffect(() => {
        if (assetsProperty?.data && !assetsPropertyIsError && !assetsPropertyLoading) {
            setAssetsPropertyData(assetsProperty?.data);
        }
    }, [assetsProperty, assetsPropertyIsError, assetsPropertyLoading]);

    useEffect(() => {
        if (assetsPropertyFormData?.data && !assetsPropertyFormDataIsError && !assetsPropertyFormDataLoading) {
            if (assetsPropertyFormData?.data?.id) {
                setSelectedRowId(assetsPropertyFormData?.data?.id)
            }
            setFormData({
                propertyHomeAddress: assetsPropertyFormData?.data?.homeAddress,
                propertyEstimatedValue: assetsPropertyFormData?.data?.estimatedValue,
                propertyDatePurchased: assetsPropertyFormData?.data?.datePurchased,
                propertyType: assetsPropertyFormData?.data?.propertyType,
                propertyDetailedType: assetsPropertyFormData?.data?.detailedType,
                propertyRentalIncome: assetsPropertyFormData?.data?.rentalIncome,
                propertyRentalIncomeDLL: assetsPropertyFormData?.data?.frequency,
                loanDetailsBorrowTypeDLL: assetsPropertyFormData?.data?.borrowType,
                loanDetailsCurrentMortgage: assetsPropertyFormData?.data?.currentMortgage,
                loanDetailsLendersName: assetsPropertyFormData?.data?.lendersName,
                loanDetailsRepayType: assetsPropertyFormData?.data?.repayType,
                loanDetailsRepayment: assetsPropertyFormData?.data?.loanDetailsRepayment,
                loanDetailsMonthlyRepayment: assetsPropertyFormData?.data?.loanDetailsMonthlyRepayment,
                loanDetailsRepaymentDLL: assetsPropertyFormData?.data?.loanFrequency,
                loanDetailsInterestRate: assetsPropertyFormData?.data?.interestRate,
                loanDetailsInterestType: assetsPropertyFormData?.data?.interestType,
                propertyUse: assetsPropertyFormData?.data?.propertyUse,
                propertyEquity1: assetsPropertyFormData?.data?.propertyEquity1,
                propertyLVR1: assetsPropertyFormData?.data?.propertyLVR1,
            })

        }
    }, [assetsPropertyFormData, assetsPropertyFormDataIsError, assetsPropertyFormDataLoading]);

    const { mutate: updateClientAssets, isPending: isUpdateClientAssetsPending } = useMutationRequestQuery(
        'insertOrUpdateAssetsPropertyService',
        insertOrUpdateAssetsPropertyService,
        {
            onSuccess: () => {
                toast.success("Assets property information saved or updated successfully.");
                setFormData(AssetsPropertyFormData)
                setSelectedRowId(null)
                refetchAssetsProperty()
                setShowForm(false)
                window.scrollTo({ top: 0, behavior: "auto" });
            },
            onError: (error: any) => {
                toast.error("Failed to submit assets property information");
                console.error(error);
            },
        }
    );

    const {
        mutate: deleteClientAssets,
    } = useMutationRequestQuery(
        'softDeleteAssetsPropertyService',
        softDeleteAssetsPropertyService,
        {
            onSuccess: () => {
                toast.success("Property deleted successfully.");
                refetchAssetsProperty()
                setSelectedRowId(null)
            },
            onError: (error: any) => {
                toast.error("Failed to delete property.");
                console.error(error);
            },
        }
    );
    const handleSubmit = (e: React.FormEvent) => {
        const requiredFields = [
            'propertyHomeAddress',
            'propertyEstimatedValue',
            'propertyDatePurchased',
            'propertyType',
            'loanDetailsCurrentMortgage',
            'loanDetailsLendersName',
            // 'loanDetailsRepayment',
            // 'loanDetailsMonthlyRepayment',
            'loanDetailsInterestRate',
            'propertyUse'
        ];

        for (let field of requiredFields) {
            if (!formData[field] || formData[field].trim() === "") {
                toast.error(`Please fill all assets liabilities fields.`);
                return;
            }
        }
        updateClientAssets(
            {
                clientID: clientId,
                id: selectedRowId ? selectedRowId : '0',
                homeAddress: formData.propertyHomeAddress,
                estimatedValue: formData.propertyEstimatedValue,
                datePurchased: formData.propertyDatePurchased,
                propertyType: formData.propertyType,
                detailedType: formData.propertyDetailedType,
                rentalIncome: formData.propertyRentalIncome,
                frequency: formData.propertyRentalIncomeDLL,
                borrowType: formData.loanDetailsBorrowTypeDLL,
                currentMortgage: formData.loanDetailsCurrentMortgage,
                lendersName: formData.loanDetailsLendersName,
                repayType: formData.loanDetailsRepayType,
                loanDetailsRepayment: formData.loanDetailsRepayment,
                loanDetailsMonthlyRepayment: formData.loanDetailsMonthlyRepayment,
                loanFrequency: formData.loanDetailsRepaymentDLL,
                interestRate: formData.loanDetailsInterestRate,
                interestType: formData.loanDetailsInterestType,
                propertyEquity1: formData.propertyEquity1,
                propertyLVR1: formData.propertyLVR1,
                propertyUse: formData.propertyUse
            }
        );
    };


    // Handle field value change
    const handleValueChange = (name: string, value: string | Date | null) => {
        setFormData((prevData: any) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const splitDropdownValues = (dropdownValues: string) => {
        return dropdownValues ? dropdownValues.split(',').map(value => value.trim()) : [];
    };

    const handleEditClick = (id: number) => {
        setShowForm(true)
        setSelectedRowId(id);
    };

    const handleDeleteClick = (id: number) => {
        deleteClientAssets(id)
    };

    useEffect(() => {
        const safeParseFloat = (value: any) => {
            const sanitizedValue = (value || '').replace(/,/g, '');
            const parsedValue = parseFloat(sanitizedValue);
            return isNaN(parsedValue) ? 0 : parsedValue;
        };

        // Calculate equity
        const equity1 = safeParseFloat(formData.propertyEstimatedValue) - safeParseFloat(formData.loanDetailsCurrentMortgage);
        // Calculate LVR and round it
        const calculateLVR = (debt: any, value: any) => {
            const debtValue = safeParseFloat(debt);
            const propertyValue = safeParseFloat(value);
            if (propertyValue <= 0) {
                return 0;
            }
            return Math.round((debtValue / propertyValue) * 100);
        };

        const lvr1 = calculateLVR(formData.loanDetailsCurrentMortgage, formData.propertyEstimatedValue);

        setFormData((prevData: any) => ({
            ...prevData,
            propertyEquity1: equity1.toString(),
            propertyLVR1: lvr1.toString(),
        }));
    }, [
        formData.propertyEstimatedValue, formData.loanDetailsCurrentMortgage,
        location
    ]);


    useEffect(() => {
        if (formData.propertyUse === "Owner Occupied") {
            setFormData({ ...formData, propertyRentalIncome: "" })
        }
    }, [formData.propertyUse, selectedRowId])

    const columns: any = [
        {
            dataField: "actions",
            text: "Action",
            formatter: (_: unknown, row: any) => (
                <UL className="action simple-list flex-row">
                    <LI className="edit"><a style={{ cursor: 'pointer' }} onClick={() => { handleEditClick(row.id) }} ><i className="icon-pencil-alt" /></a></LI>
                    <LI className="delete"><a style={{ cursor: 'pointer' }} onClick={() => { handleDeleteClick(row.id) }} ><i className="icon-trash" /></a></LI>
                </UL>
            ),
            headerStyle: { width: "5rem", color: "#0000ff" }, // Blue header
        },
        {
            dataField: "propertyType",
            text: "Type",
            headerStyle: { width: "7rem", color: "#0000ff" },
        },
        {
            dataField: "homeAddress",
            text: "Location",
            headerStyle: { width: "20rem", color: "#0000ff" },
        },
        {
            dataField: "propertyUse",
            text: "Usage",
            headerStyle: { width: "9rem", color: "#0000ff" },
        },
        {
            dataField: "datePurchased",
            text: "Acquired",
            formatter: (cell: string) => new Date(cell).toLocaleDateString("en-GB"),
            headerStyle: { width: "8rem", color: "#0000ff" },
        },
        {
            dataField: "estimatedValue",
            text: "Value",
            headerStyle: { width: "6rem", color: "#0000ff" },
        },
        {
            dataField: "currentMortgage",
            text: "Debt",
            headerStyle: { width: "6rem", color: "#0000ff" },
        },
        {
            dataField: "rentalIncome",
            text: "Rent Mthly",
            headerStyle: { width: "9rem", color: "#0000ff" },
        },
        {
            dataField: "lendersName",
            text: "Lender",
            headerStyle: { color: "#0000ff" },
        },
        {
            dataField: "loanDetailsMonthlyRepayment",
            text: "Repay Mthly",
            headerStyle: { width: "8rem", color: "#0000ff" },
        },
        {
            dataField: "interestRate",
            text: "%",
            headerStyle: { width: "4rem", color: "#0000ff" },
        },
        {
            dataField: "propertyLVR1",
            text: "LVR",
            formatter: (cell?: number) => cell ?? 0,
            headerStyle: { width: "4rem", color: "#0000ff" },
        },
        {
            dataField: "propertyEquity1",
            text: "Equity",
            formatter: (cell?: number) => cell ?? 0,
            headerStyle: { color: "#0000ff" },
        }
    ];
    const isOwnerOccupied = (currentProperty?: any) => {
        const alreadyOwnerOccupied = assetsPropertyData.some(
            (property: any) => property.propertyUse === "Owner Occupied"
        );

        if (currentProperty?.propertyUse === "Owner Occupied") {
            return false;
        }

        return alreadyOwnerOccupied;
    };

    const handleToggle = () => {
        setShowForm(!showForm)
    }

    const handleCancel = () => {
        handleToggle();
        setSelectedRowId(null)
        window.scrollTo({ top: 0, behavior: "auto" });
        setFormData(AssetsPropertyFormData)
    };

    return (
        <>
            <div className={isMMPAssetsLiabilitiesRoute ? 'mmp-assets-liabilities container-fluid' : 'container-fluid'}>
                <div className="table-responsive h-100 py-4">
                    <section className='bg-white'>

                        <div className="assets-scrollable-table-container">
                            <table className="scrollable-table" style={{ marginLeft: '1%', marginTop: '3%' }}>
                                <div className="d-flex align-items-center">
                                    <Col xs={12} className='d-flex'>
                                        <H3 className="heading-color f-w-900 text-left my-2">Assets - Property</H3>
                                    </Col>
                                </div>
                                {assetsPropertyLoading ? (
                                    <div
                                        style={{
                                            borderRadius: '8px',
                                            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                                            padding: '15px',
                                            backgroundColor: '#fff',
                                            textAlign: 'center',
                                        }}
                                        className='mt-4'
                                    >
                                        <Spinner color="primary" />
                                    </div>
                                ) : assetsPropertyData.length !== 0 ? (
                                    <div
                                        style={{
                                            borderRadius: '8px',
                                            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                                            padding: '5px',
                                            backgroundColor: '#fff',
                                            overflowX: 'auto', // Enables horizontal scrolling when needed
                                            width: '100%', // Ensures container takes full width
                                        }}
                                        className='mt-4'
                                    >
                                        <div style={{ minWidth: 'max-content' }}> {/* Ensures table does not shrink */}
                                            <BootstrapTable
                                                keyField="id"
                                                data={[...assetsPropertyData].sort((a: any, b) => (a.propertyUse === "Owner Occupied" ? -1 : 1))}
                                                columns={columns}
                                                bordered={false}
                                                wrapperClasses="table-responsive"
                                                rowStyle={(row) => ({
                                                    fontWeight: row.propertyUse === "Owner Occupied" ? "bold" : "normal",
                                                })}
                                            />
                                        </div>
                                    </div>

                                ) : (
                                    <div
                                        style={{
                                            borderRadius: '8px',
                                            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                                            padding: '15px',
                                            backgroundColor: '#fff',
                                            textAlign: 'center',
                                        }}
                                        className='mt-4'
                                    >
                                        <h6>No property has been added yet.</h6>
                                    </div>
                                )}

                                {showForm && <Row className="gx-2 mt-4">
                                    {/* Property Home Address */}
                                    <Col sm={10} className='sm-row' style={{ width: '70%' }}>
                                        <TextField
                                            label="Location"
                                            type="text"
                                            name="propertyHomeAddress"
                                            value={formData.propertyHomeAddress}
                                            setValue={handleValueChange}
                                            placeholder="Enter property home address"
                                            design='flex'
                                            maxChars={70}
                                            enableAddressFinder={true}
                                            passCol='col-4'
                                            tabIndex={1}
                                        />
                                    </Col>

                                    <Col sm={10} className='sm-row' style={{ width: '70%' }} >
                                        <Dropdown
                                            label="Type"
                                            name="propertyType"
                                            value={formData.propertyType}
                                            setValue={handleValueChange}
                                            options={splitDropdownValues(clientFactsFindData ? clientFactsFindData[16].dropDownValues : '')}
                                            design='flex'
                                            tabIndex={2}
                                        />
                                    </Col>

                                    <Col sm={10} className='sm-row' style={{ width: '70%' }}>
                                        <Dropdown
                                            label="Property Use"
                                            name="propertyUse"
                                            value={formData.propertyUse}
                                            setValue={handleValueChange}
                                            options={isOwnerOccupied(formData) ? ["Investment"] : ["Owner Occupied", "Investment"]}
                                            design='flex'
                                            tabIndex={3}
                                        />
                                    </Col>

                                    <Col sm={10} className='sm-row' style={{ width: '70%' }}>
                                        <DatePicker
                                            label="Purchase Date"
                                            name="propertyDatePurchased"
                                            value={formData.propertyDatePurchased}
                                            setValue={handleValueChange as any}
                                            design='flex'
                                            passCol='col-'
                                            tabIndex={4}
                                        />
                                    </Col>


                                    <Col sm={10} className='sm-row' style={{ width: '70%' }}>
                                        <DollarInput
                                            label="Value"
                                            name="propertyEstimatedValue"
                                            value={formData.propertyEstimatedValue}
                                            setValue={handleValueChange}
                                            placeholder="Enter estimated value"
                                            // required={true}
                                            limit={8}
                                            design='flex'
                                            passCol='col-2'
                                            tabIndex={5}
                                        />
                                    </Col>

                                    <Col sm={10} className='sm-row' style={{ width: '70%' }}>
                                        <DollarInput
                                            label="Debt"
                                            name="loanDetailsCurrentMortgage"
                                            value={formData.loanDetailsCurrentMortgage}
                                            setValue={handleValueChange}
                                            placeholder="Enter amount"
                                            limit={8}
                                            design='flex'
                                            passCol='col-2'
                                            tabIndex={6}
                                        />
                                    </Col>

                                    {/* Rental Income */}
                                    <Col sm={10} className='sm-row' style={{ width: '70%' }}>
                                        <DollarInput
                                            label="Rent Monthly"
                                            name="propertyRentalIncome"
                                            value={formData.propertyRentalIncome}
                                            setValue={handleValueChange}
                                            placeholder="Enter rental income"
                                            limit={5}
                                            design='flex'
                                            disabled={formData.propertyUse === "Owner Occupied"}
                                            passCol='col-2'
                                            tabIndex={7}
                                        />
                                    </Col>

                                    <Col sm={10} className='sm-row' style={{ width: '70%' }}>
                                        <TextField
                                            label="Lender"
                                            type="text"
                                            name="loanDetailsLendersName"
                                            value={formData.loanDetailsLendersName}
                                            setValue={handleValueChange}
                                            placeholder="Enter lender's name"
                                            design='flex'
                                            passCol='col-2'
                                            maxChars={20}
                                            tabIndex={8}
                                        />
                                    </Col>

                                    <Col sm={10} className='sm-row' style={{ width: '70%' }}>
                                        <DollarInput
                                            label="Monthly Repayment"
                                            name="loanDetailsMonthlyRepayment"
                                            value={formData.loanDetailsMonthlyRepayment}
                                            setValue={handleValueChange}
                                            placeholder="Enter amount"
                                            limit={5}
                                            design='flex'
                                            passCol='col-2'
                                            tabIndex={9}
                                        />
                                    </Col>

                                    <Col sm={10} className='sm-row' style={{ width: '70%' }}>
                                        <DecimalInput
                                            label="Loan %"
                                            name="loanDetailsInterestRate"
                                            value={formData.loanDetailsInterestRate}
                                            setValue={handleValueChange}
                                            placeholder=""
                                            limit={4}
                                            design='flex'
                                            passCol='col-2'
                                            tabIndex={10}
                                        />
                                    </Col>
                                    <Col sm={10} className='sm-row' style={{ width: '70%' }}>
                                        <TextField
                                            label="LVR"
                                            type="text"
                                            name="propertyLVR1"
                                            value={formData.propertyLVR1}
                                            style={{ color: '#00ce00' }}
                                            setValue={handleValueChange}
                                            placeholder=""
                                            design="flex"
                                            disabled
                                            maxChars={20}
                                        />
                                    </Col>
                                    <Col sm={10} className='sm-row' style={{ width: '70%' }}>
                                        <TextField
                                            label="Equity"
                                            type="text"
                                            name="propertyEquity1"
                                            value={formData.propertyEquity1}
                                            setValue={handleValueChange}
                                            placeholder=""
                                            disabled
                                            design='flex'
                                            maxChars={20}
                                        />
                                    </Col>
                                    {/* Loan Details Interest Rate */}

                                </Row>}
                            </table>
                            {
                                !showForm ? (
                                    <>
                                        <Button
                                            disabled={isUpdateClientAssetsPending}
                                            type="button"
                                            color="primary"
                                            onClick={handleToggle}
                                            className="my-3 mx-3"
                                            style={{ fontSize: "16px" }}
                                        >
                                            Add New Property
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <Button
                                            disabled={isUpdateClientAssetsPending}
                                            type="button"
                                            color="primary"
                                            onClick={handleSubmit}
                                            className="my-3 mx-3"
                                            style={{ fontSize: "16px" }}
                                        >
                                            {isUpdateClientAssetsPending ? "Loading . . ." : selectedRowId ? "Update Property" : "Add New Property"}
                                        </Button>
                                        {!selectedRowId && <Button
                                            type="button"
                                            color="secondary"
                                            onClick={handleCancel}
                                            className="my-3"
                                            style={{ fontSize: "16px", width: '180px' }}
                                        >
                                            Cancel
                                        </Button>}
                                    </>
                                )
                            }
                        </div>
                    </section>
                </div>
            </div>
            <FactFindAssetsOther  clientId={clientId}/>
            <FactFindMotorVehicles clientFactsFindData={clientFactsFindData} clientId={clientId} />
            <FactFindLiabilitiesOthers clientFactsFindData={clientFactsFindData} clientId={clientId} />
            <FactFindIncomeExpense clientId={clientId} />
        </>
    );
}